// ABOUTME: Tracks the selected candidate identity in list-backed applicant modals.
// ABOUTME: Reconciles that identity with refreshed list positions without switching candidates.
export interface ApplicantListModalSelection {
    applicantId: string;
    index: number;
    page: number;
}

export interface ApplicantListModalPageChange {
    fieldsAtRequest: Compleo.IObject[];
    index: number;
    page: number;
}

type ApplicantIdReader = (field: Compleo.IObject) => unknown;

const normalizeApplicantId = (value: unknown) => {
    if (typeof value !== "string") {
        return "";
    }

    return value.split(":").pop() || value;
};

export const createApplicantListModalSelection = (
    fields: Compleo.IObject[],
    index: number,
    page: number,
    readApplicantId: ApplicantIdReader
): ApplicantListModalSelection => {
    const field = fields[index];

    return {
        applicantId: field ? normalizeApplicantId(readApplicantId(field)) : "",
        index,
        page
    };
};

export const createApplicantListModalPageChange = (
    fieldsAtRequest: Compleo.IObject[],
    index: number,
    page: number
): ApplicantListModalPageChange => ({ fieldsAtRequest, index, page });

export const canNavigateApplicantListModal = (
    selection: ApplicantListModalSelection,
    pageChange: ApplicantListModalPageChange | null
) => selection.index >= 0 && pageChange === null;

export const resolveApplicantListModalPageChange = (
    pageChange: ApplicantListModalPageChange,
    fields: Compleo.IObject[],
    currentPage: number,
    loadingData: boolean,
    readApplicantId: ApplicantIdReader,
    requestError = false
) => {
    if (requestError) {
        return null;
    }

    if (
        loadingData ||
        currentPage !== pageChange.page ||
        fields === pageChange.fieldsAtRequest
    ) {
        return undefined;
    }

    return createApplicantListModalSelection(
        fields,
        pageChange.index,
        pageChange.page,
        readApplicantId
    );
};

export const resolveApplicantListModalSelection = (
    selection: ApplicantListModalSelection,
    fields: Compleo.IObject[],
    readApplicantId: ApplicantIdReader
) => {
    if (!selection.applicantId) {
        return fields[selection.index]
            ? createApplicantListModalSelection(
                  fields,
                  selection.index,
                  selection.page,
                  readApplicantId
              )
            : selection;
    }

    const currentField = fields[selection.index];
    if (
        currentField &&
        normalizeApplicantId(readApplicantId(currentField)) ===
            selection.applicantId
    ) {
        return selection;
    }

    const index = fields.findIndex(
        (field) =>
            normalizeApplicantId(readApplicantId(field)) ===
            selection.applicantId
    );

    if (index >= 0) {
        return index !== selection.index ? { ...selection, index } : selection;
    }

    return selection.index === -1 ? selection : { ...selection, index: -1 };
};
